import { Component, ElementRef, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { PDFDocumentProxy } from 'pdfjs-dist';
import { Content, IonicPage, ModalController, Navbar, NavController, NavParams, ToastController } from 'ionic-angular';
import { PtLuxonDate } from '../../../models/projects/date.models';
import { DailyProgressReportService } from '../../../services/daily-progress-report/daily-progress-report.service';
import {
  DailyProgressReportContext,
  DailyProgressReportRecord,
  DailyProgressReportSummary
} from '../../../models/daily-progress-report/daily-progress-report.models';
import { State } from '@progress/kendo-data-query';
import { DailyLogService } from '../../../services/daily-log/daily-log.service';
import { columns } from './daily-progress-report-preview.config';
import { CompleteDayService } from '../../../services/complete-day/complete-day.service';
import { CompleteDayEditor } from '../../../models/complete-day.models';
import { VesselService } from '../../../services/vessel/vessel.service';
import { LockedDayService } from '../../../services/locked-day/locked-day.service';
import { DbContext, DbTable } from '../../../services/data/dbcontext.service';
import { NavHelperService } from '../../../services/nav/nav-helper.service';
import { DailyLogPermissions } from '../../../models/permissions/permission.models';
import { PermissionsService } from '../../../services/permissions/permissions.service';
import { DailyLogPage } from '../../daily-log/daily-log/daily-log';
import { AppConfig } from '../../../app/app.config';
import { ConfirmationModalComponent } from '../../daily-log/confirmation-modal/confirmation-modal.component';
import { SignatureDateModalComponent } from '../signature-date-modal/signature-date-modal.component';
import { DateTime } from 'luxon';
import { SupportContextKey } from '../../../models/support/support.models';
import { SupportContextService } from '../../../services/support/support-context.service';
import { ErrorsComponent } from '../../shared/components/errors/errors.component';
import { ImplementerService } from '../../../services/implementer/implementer.service';
import { DateService } from '../../../services/date/date.service';

@IonicPage()
@Component({
  selector: 'page-daily-progress-report-preview',
  templateUrl: 'daily-progress-report-preview.html',
})
export class DailyProgressReportPreviewPage {
  @ViewChild('dprForm') dprForm: ElementRef;
  @ViewChild('validation') validation: ErrorsComponent;
  @ViewChild(Navbar) navbar: Navbar;
  @ViewChild(Content) content: Content;

  config = AppConfig.settings;
  summaryLoading: boolean;
  previewLoading: boolean;
  lockedDay: boolean;
  progressReportSummary: DailyProgressReportSummary;
  ptLuxonDate: PtLuxonDate;
  displayDate: string;
  displayDay: string;
  currentVersionNumber: string;
  currentVersionLoading = true;
  private initialReportContext: DailyProgressReportContext;
  private reportSummaryDate: number;
  columns = columns;
  state: State;

  completeDay: boolean;
  currentVesselId: number;
  currentVesselName: string;
  currentSetupConfigured: boolean;
  completeDayLoading: boolean;
  completeDayError: string;
  workflowLoading: boolean = true;
  workflowCheckFailed: boolean = false;
  prerequisiteState: 'checking' | 'available' | 'blocked' = 'checking';
  private workflowRequestId = 0;
  private validationSummaryDate: number;
  private workflowDate: DateTime;
  previousDayDate: DateTime;
  isActiveDay: boolean = false;
  canContinueDay: boolean = false;
  existingNextDayDate: DateTime;
  canCompleteReviewedDay: boolean = false;
  continuingDay: boolean = false;
  implementerBlocking: boolean = false;
  // The choice screen precedes the two wizard steps: checks (2) and preview (3).
  step: number = 1;
  previewVisited: boolean = false;
  completedSuccessfully: boolean = false;
  previewError: string;
  previewVersionNumber: string;
  previewVersionLoading: boolean = false;
  previewDownloadLoading: boolean = false;
  currentDownloadLoading: boolean = false;
  reportContextError: string;
  private reportContextStale = false;
  private reportContextRequestId = 0;
  private previewStale = false;
  private previewRequest: Promise<void>;
  private previewRequestId = 0;
  private previewObjectUrl: string;
  completionReady = false;
  private completionDelayTimer: ReturnType<typeof setTimeout>;

  private startCompletionDelay() {
    this.clearCompletionDelay();
    this.completionDelayTimer = setTimeout(() => {
      this.completionReady = this.step === 3;
      this.completionDelayTimer = null;
    }, 2000);
  }

  private clearCompletionDelay() {
    clearTimeout(this.completionDelayTimer);
    this.completionDelayTimer = null;
    this.completionReady = false;
  }

  ngOnDestroy() {
    this.clearCompletionDelay();
    this.invalidatePreview();
  }

  private invalidatePreview() {
    this.previewRequestId++;
    this.previewRequest = null;
    this.previewStale = true;
    this.pdfPreviewUrl = null;
    if (this.previewObjectUrl) { URL.revokeObjectURL(this.previewObjectUrl); }
    this.previewObjectUrl = null;
  }

  private preloadPreview(): Promise<void> {
    if (!this.currentVesselId || !this.currentSetupConfigured || !this.canViewReport
      || this.summaryLoading || this.reportContextError) { return Promise.resolve(); }
    if (this.previewRequest) { return this.previewRequest; }
    if (!this.previewStale && (this.pdfPreviewUrl || this.previewError)) { return Promise.resolve(); }
    return this.loadPreviewViewer();
  }

  get canPreview(): boolean {
    return !this.endOfDayActionsDisabled && !!this.currentVesselId && !!this.currentSetupConfigured
      && this.canViewReport && !this.summaryLoading && !this.reportContextError;
  }
  get hasCurrentDpr(): boolean { return !!this.currentVersionNumber && this.currentVersionNumber !== 'No Current Version'; }
  get completionBlockedReason(): string {
    if (this.busy) { return ''; }
    if (this.workflowLoading || this.validation?.validationLoading) { return 'Checking completion requirements...'; }
    if (this.workflowCheckFailed || !this.workflowDateIsCurrent) { return 'Refresh the checks for this reporting day before completing.'; }
    if (this.errorCount || !this.validation?.validationResult?.isValid) { return 'Resolve all errors in Review Checks before completing this day. Warnings do not prevent completion.'; }
    if (!this.permissions.canCompleteDay) { return 'You do not have permission to complete this day.'; }
    if (this.implementerBlocking) { return 'Finish the active implementer session before completing this day.'; }
    if (!this.canCompleteReviewedDay) { return 'This day must be editable and the previous day must have a completed DPR.'; }
    if (!this.currentSetupConfigured || !this.currentVesselId || this.summaryLoading || this.reportContextError) { return 'Report information must be available before completing this day.'; }
    if (!this.completionReady) { return 'Completion will be available shortly.'; }
    return '';
  }

  get busy(): boolean { return this.completeDayLoading || this.continuingDay; }
  private get workflowDateIsCurrent(): boolean {
    const selectedDate = this.navParams.get('date') || this.ptLuxonDate;
    return !!this.workflowDate && this.workflowDate.toMillis() === this.ptLuxonDate?.dateTime.toMillis()
      && this.workflowDate.toMillis() === selectedDate?.dateTime.toMillis();
  }
  get previousDayBlocked(): boolean { return this.prerequisiteState === 'blocked' && this.workflowDateIsCurrent; }
  get endOfDayActionsDisabled(): boolean {
    return this.busy || this.workflowLoading || this.workflowCheckFailed
      || this.prerequisiteState !== 'available' || !this.workflowDateIsCurrent;
  }
  get previousDayDisplayDate(): string {
    if (!this.previousDayDate) { return ''; }
    const day = this.previousDayDate.day;
    const suffix = day >= 11 && day <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
    return `${this.previousDayDate.toFormat('EEEE')} ${day}${suffix} ${this.previousDayDate.toFormat('MMMM')}`;
  }
  get hasValidationSummary(): boolean {
    const selectedDate = this.navParams.get('date') || this.ptLuxonDate;
    return this.validationSummaryDate != null
      && this.validationSummaryDate === this.ptLuxonDate?.dateTime.toMillis()
      && this.validationSummaryDate === selectedDate?.dateTime.toMillis();
  }

  get warningCount(): number { return this.validation?.warnings?.length || 0; }
  get errorCount(): number { return this.validation?.errors?.length || 0; }
  get canViewReport(): boolean { return this.permissions.canViewDailyLog || this.permissions.canManageDailyLog || this.permissions.canViewDailyProgressReports; }
  get reportLocationLabel(): string {
    const setup = this.progressReportSummary?.updatedSetupSummary;
    return setup?.transitingLocationId ? (setup.transitOperationIsJacking ? 'Jacking At' : 'Transiting To') : 'Location';
  }
  get reportLocation(): string {
    const setup = this.progressReportSummary?.updatedSetupSummary;
    return setup?.transitingLocationId ? (setup.transitOperationIsJacking ? setup.jackingLocationName : setup.transitingLocationName) : setup?.locationName;
  }
  previewFailed() {
    this.previewLoading = false;
    this.previewVersionLoading = false;
    this.previewError = 'The report preview could not be loaded. Retry or download the preview to review it.';
  }

  reviewChecks() {
    if (this.endOfDayActionsDisabled) { return; }
    this.completeDayError = null;
    this.step = 2;
    this.scrollToStart();
    this.preloadPreview();
  }

  async previewReport() {
    if (!this.canPreview) { return; }
    if (this.step !== 3) { this.startCompletionDelay(); }
    this.step = 3;
    this.previewVisited = true;
    this.scrollToStart();
    await this.preloadPreview();
  }

  back() {
    if (!this.busy && this.step > 1) { this.clearCompletionDelay(); this.step--; this.scrollToStart(); }
  }

  private scrollToStart() {
    if (this.content) {
      this.content.scrollToTop(0);
      this.resizeContent();
    }
  }

  private resizeContent() {
    // Re-measure after Angular renders footer status changes so it cannot cover content.
    if (this.content) { setTimeout(() => this.content.resize()); }
  }

  async cancel() {
    if (!this.busy) { await this.navCtrl.pop(); }
  }

  async returnToLog(date: DateTime = this.ptLuxonDate.dateTime) { await this.navCtrl.setRoot(DailyLogPage, { inputDate: date }); }

  async openPreviousDayLog() {
    if (this.busy || !this.previousDayBlocked) { return; }
    this.navHelperService.updateDailyLogRequiresReload(true);
    await this.navCtrl.setRoot(DailyLogPage, { inputDate: this.previousDayDate });
  }

  private saveDprFile(file: { report: Blob; filename: string }) {
    const url = URL.createObjectURL(file.report);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.filename;
    try { link.click(); }
    finally { setTimeout(() => URL.revokeObjectURL(url), 60000); }
  }

  pdfPreviewUrl: string;
  pdfDownloadUrl: string;

  permissions: DailyLogPermissions = new DailyLogPermissions();


  constructor(
    private navCtrl: NavController,
    private navParams: NavParams,
    private dprService: DailyProgressReportService,
    private dailyLogService: DailyLogService,
    private completeDayService: CompleteDayService,
    private vesselService: VesselService,
    private navHelperService: NavHelperService,
    private permissionService: PermissionsService,
    public modalController: ModalController,
    private supportContextService: SupportContextService,
    private implementerService: ImplementerService,
    private dateService: DateService,
    private toastCtrl: ToastController,
  ) {
    this.completeDay = this.navParams.get('completeDay');
    const context: DailyProgressReportContext = this.navParams.get('reportContext');
    const date: PtLuxonDate = this.navParams.get('date');
    if (!this.completeDay && context?.summary && context.dateMillis === date?.dateTime?.toMillis()) {
      this.initialReportContext = cloneDeep(context);
      this.progressReportSummary = this.initialReportContext.summary;
      this.reportSummaryDate = context.dateMillis;
      this.currentVesselName = context.summary.vesselName;
      this.ptLuxonDate = new PtLuxonDate(date.dateTime);
      this.setDisplayDate();
    }
    this.navHelperService.currentSetupConfigured.subscribe(currentSetupConfigured => {
      this.currentSetupConfigured = currentSetupConfigured;
    });
  }

  async ngOnInit() {
    this.supportContextService.setComponentContext(SupportContextKey.CompleteDay, this.completeDay ? 'End of Day' : 'DPR Preview', 'DailyProgressReportPreviewPage');

    this.permissions = await this.permissionService.dailyLogPermissions();
  }

  async ionViewDidLoad() {
    if (this.completeDay) {
      this.navbar.backButtonClick = () => {
        if (this.busy) { return; }
        if (this.completedSuccessfully) { this.returnToLog(); }
        else if (this.step > 1) { this.back(); }
        else { this.cancel(); }
      };
    }
    this.ptLuxonDate = this.navParams.get('date') || await this.dailyLogService.getCurrentLogDay();
    await this.setDisplayDate();

    await this.loadReportContext();
    this.state = this.dailyLogService.getState();
  }

  async loadReportContext() {
    if (!this.currentSetupConfigured) { return; }
    const date = new PtLuxonDate(this.ptLuxonDate.dateTime);
    const requestId = ++this.reportContextRequestId;
    const isCurrent = () => requestId === this.reportContextRequestId
      && date.dateTime.toMillis() === this.ptLuxonDate.dateTime.toMillis();
    const initialContext = this.initialReportContext?.dateMillis === date.dateTime.toMillis() ? this.initialReportContext : null;
    // Use navigation data once; retries and later refreshes fetch fresh context.
    this.initialReportContext = null;
    if (this.reportSummaryDate != null && this.reportSummaryDate !== date.dateTime.toMillis()) {
      this.progressReportSummary = null;
      this.currentVesselName = null;
    }
    this.summaryLoading = true;
    this.currentVersionLoading = true;
    this.reportContextError = null;
    try {
      const [editor, summary, version] = await Promise.all([
        this.vesselService.getEditor(),
        this.dprService.getDprSummary(date, initialContext?.summary.updatedSetupSummary),
        this.dprService.getCurrentDprVersionNumber(date).then(result => result.toPromise())
      ]);
      if (!isCurrent()) { return; }
      this.currentVesselName = editor.name || summary.vesselName;
      this.currentVesselId = editor.id;
      this.progressReportSummary = summary;
      this.reportSummaryDate = date.dateTime.toMillis();
      this.currentVersionNumber = version;
      if (!this.completeDay) { await this.loadPreviewViewer(); }
    } catch {
      if (isCurrent()) { this.reportContextError = 'Unable to load report information. Please try again.'; }
    } finally {
      if (isCurrent()) {
        this.summaryLoading = false;
        this.currentVersionLoading = false;
        if (this.completeDay && this.step > 1 && !this.workflowLoading) { this.preloadPreview(); }
      }
    }
  }

  ionViewWillEnter() {
    if (this.completeDay && this.step === 3) { this.startCompletionDelay(); }
    if (this.completeDay && !this.completedSuccessfully) {
      this.resetWorkflowCheck();
    }
  }

  ionViewWillLeave() {
    this.clearCompletionDelay();
    if (this.completeDay && !this.completedSuccessfully) {
      this.reportContextStale = true;
      this.invalidatePreview();
      this.resetWorkflowCheck();
    }
  }

  private resetWorkflowCheck() {
    this.workflowRequestId++;
    this.prerequisiteState = 'checking';
    this.workflowLoading = true;
    this.workflowCheckFailed = false;
    this.workflowDate = null;
    this.previousDayDate = null;
    this.canCompleteReviewedDay = false;
    this.canContinueDay = false;
    this.existingNextDayDate = null;
  }

  async ionViewDidEnter() {
    await this.refreshWorkflow();
  }

  async refreshWorkflow() {
    if (this.completedSuccessfully) { return; }
    if (!this.completeDay) {
      this.workflowLoading = false;
      return;
    }
    if (this.busy) { return; }
    this.resetWorkflowCheck();
    const requestId = this.workflowRequestId;
    this.completeDayError = null;
    let date: PtLuxonDate;
    const isCurrent = () => requestId === this.workflowRequestId && (!date || (
      date.dateTime.toMillis() === this.ptLuxonDate?.dateTime.toMillis()
      && date.dateTime.toMillis() === (this.navParams.get('date') || this.ptLuxonDate)?.dateTime.toMillis()
    ));
    try {
      const selectedDate = this.navParams.get('date') || await this.dailyLogService.getCurrentLogDay();
      if (!isCurrent()) { return; }
      // Snapshot the date so mutations of a shared navigation parameter cannot
      // make an old response appear to belong to the newly selected day.
      date = new PtLuxonDate(selectedDate.dateTime);
      const dateChanged = this.ptLuxonDate && this.ptLuxonDate.dateTime.toMillis() !== selectedDate.dateTime.toMillis();
      this.ptLuxonDate = new PtLuxonDate(selectedDate.dateTime);
      if (this.reportContextStale || dateChanged) {
        this.reportContextStale = false;
        await this.loadReportContext();
        if (!isCurrent()) { return; }
      }
      if (!isCurrent()) { return; }
      await this.setDisplayDate();
      this.validation.dailyLogDate = date;
      await this.validation.refreshErrors();
      if (!isCurrent()) { return; }
      this.validationSummaryDate = date.dateTime.toMillis();
      const completed = await this.completeDayService.checkDay({ date: date.dateTime });
      if (!isCurrent()) { return; }
      let canComplete: boolean;
      let canContinue = false;
      let isActiveDay = false;
      let implementerBlocking = false;
      let previousDayDate: DateTime = null;
      let existingNextDayDate: DateTime = null;
      if (this.config.isImplementerEnvironment) {
        canComplete = !completed && await this.implementerService.implementerIsActive(this.config.isVesselEnvironment, date);
      } else {
        const workflow = await this.completeDayService.getWorkflow(date);
        if (!isCurrent()) { return; }
        const activeDate = DateTime.fromISO(workflow.activeDate);
        isActiveDay = date.dateTime.toMillis() === activeDate.toMillis();
        if (date.dateTime.toMillis() < activeDate.toMillis()) {
          const nextDate = await this.dateService.addDuration(date.dateTime, { day: 1 }, true);
          if (!isCurrent()) { return; }
          if (nextDate.toMillis() > date.dateTime.toMillis() && nextDate.toMillis() <= activeDate.toMillis()) {
            existingNextDayDate = nextDate;
          }
        }
        // Only an active day's CanContinue result represents the previous-day prerequisite.
        // Historical days and implementer sessions have separate eligibility rules.
        if (isActiveDay && !workflow.canContinue) {
          previousDayDate = await this.dateService.minusDuration(date.dateTime, { day: 1 }, true);
          if (!isCurrent()) { return; }
        }
        implementerBlocking = this.config.isVesselEnvironment && !!await this.implementerService.getActiveSnapshotSummary(true);
        canContinue = workflow.canContinue && !implementerBlocking;
        canComplete = !completed && workflow.canEdit && !implementerBlocking && (!isActiveDay || workflow.canContinue);
      }
      if (!isCurrent()) { return; }
      this.workflowDate = date.dateTime;
      this.previousDayDate = previousDayDate;
      this.isActiveDay = isActiveDay;
      this.implementerBlocking = implementerBlocking;
      this.canContinueDay = canContinue;
      this.existingNextDayDate = existingNextDayDate;
      this.canCompleteReviewedDay = canComplete;
      this.prerequisiteState = previousDayDate ? 'blocked' : 'available';
      if (this.previewVisited || this.step === 2) {
        this.invalidatePreview();
        this.preloadPreview();
      }
    } catch (error) {
      if (!isCurrent()) { return; }
      this.workflowCheckFailed = true;
      this.prerequisiteState = 'checking';
      this.previousDayDate = null;
      this.canCompleteReviewedDay = false;
      this.canContinueDay = false;
      this.completeDayError = 'Unable to check this day. Please try again.';
    } finally {
      if (isCurrent()) { this.workflowLoading = false; this.resizeContent(); }
    }
  }

  get canGenerateAndComplete(): boolean {
    return this.completeDay && this.step === 3 && this.completionReady && !this.completedSuccessfully
      && this.canCompleteReviewedDay && this.permissions.canCompleteDay
      && !this.endOfDayActionsDisabled
      && !!this.currentVesselId && !!this.currentSetupConfigured && !this.summaryLoading && !this.reportContextError
      && !!this.validation && !this.validation.validationLoading && !this.errorCount && !!this.validation.validationResult?.isValid;
  }

  async continueToNextDay() {
    if (this.endOfDayActionsDisabled) { return; }
    // Returning to an existing day is navigation, not another reporting-day advance.
    if (this.completeDay && !this.config.isImplementerEnvironment && this.existingNextDayDate
      && (this.permissions.canViewDailyLog || this.permissions.canManageDailyLog)) {
      this.continuingDay = true;
      this.completeDayError = null;
      try {
        this.navHelperService.updateDailyLogRequiresReload(true);
        await this.navCtrl.setRoot(DailyLogPage, { inputDate: this.existingNextDayDate });
      } catch {
        this.completeDayError = 'Unable to open the next reporting day. Please try again.';
      } finally {
        this.continuingDay = false;
      }
      return;
    }
    if (!this.completeDay || this.config.isImplementerEnvironment || !this.isActiveDay || !this.canContinueDay
      || !this.permissions.canCompleteDay || this.workflowLoading || this.continuingDay || this.completeDayLoading) {
      this.completeDayError = this.implementerBlocking ? 'Finish the active implementer session before continuing.'
        : this.config.isImplementerEnvironment ? 'Continue to Next Day is unavailable in implementer mode. Use Implementer Navigator to open another day in this session.'
        : !this.isActiveDay ? 'Only the current reporting day can be advanced.'
        : !this.permissions.canCompleteDay ? 'You do not have permission to advance the reporting day.'
        : 'Complete the previous day and generate its DPR before continuing.';
      return;
    }
    this.continuingDay = true;
    this.completeDayError = null;
    try {
      const date = await this.completeDayService.continueDay(this.ptLuxonDate);
      this.dailyLogService.setCurrentLogDay(date);
      this.navHelperService.updateDailyLogRequiresReload(true);
      await this.navCtrl.setRoot(DailyLogPage);
    } catch (error) {
      this.completeDayError = 'Unable to continue. Complete the previous day and generate its DPR, then try again.';
    } finally {
      this.continuingDay = false;
    }
  }

  async loadPreviewViewer() {
    // Standalone preview keeps its existing URL-based viewer behavior.
    if (!this.completeDay) {
      this.previewLoading = true;
      this.previewVersionNumber = null;
      this.previewVersionLoading = false;
      this.previewError = null;
      this.pdfPreviewUrl = this.dprService.getPreviewDprDownloadLink(this.ptLuxonDate, this.currentVesselId, this.config.isImplementerEnvironment)
        + '?review=' + Date.now();
      return;
    }
    if (this.previewRequest) { return this.previewRequest; }
    this.invalidatePreview();
    const requestId = this.previewRequestId;
    const date = new PtLuxonDate(this.ptLuxonDate.dateTime);
    const isCurrent = () => requestId === this.previewRequestId
      && date.dateTime.toMillis() === this.ptLuxonDate.dateTime.toMillis();
    this.previewStale = false;
    this.previewLoading = true;
    this.previewVersionNumber = null;
    this.previewVersionLoading = false;
    this.previewError = null;
    const request = (async () => {
      try {
        const file = await this.dprService.getDprPreviewFile(date, this.currentVesselId, this.config.isImplementerEnvironment, true);
        if (!isCurrent()) { return; }
        this.previewObjectUrl = URL.createObjectURL(file.report);
        this.pdfPreviewUrl = this.previewObjectUrl;
        // The existing viewer clears loading once it has parsed the retrieved PDF.
      } catch {
        if (isCurrent()) { this.previewFailed(); }
      }
    })();
    this.previewRequest = request;
    try { await request; }
    finally { if (isCurrent()) { this.previewRequest = null; } }
  }

  async previewLoaded(pdf?: PDFDocumentProxy) {
    this.previewLoading = false;
    if (!pdf) { return; }
    const previewUrl = this.pdfPreviewUrl;
    this.previewVersionLoading = true;
    try {
      // Read the server-rendered header from the loaded document; do not infer
      // the preview version from the current version or request another report.
      const page = await pdf.getPage(1);
      const text = await page.getTextContent();
      const matches = text.items.map(item => item.str).join(' ').match(/\bVersion\s*:\s*\d+\.\d+\b/gi) || [];
      const versions = matches.map(match => match.replace(/^Version\s*:\s*/i, ''));
      if (previewUrl === this.pdfPreviewUrl) {
        this.previewVersionNumber = versions.length && versions.every(version => version === versions[0]) ? versions[0] : null;
      }
    } catch {
      // Version information is supplementary; the PDF and downloads remain usable.
      if (previewUrl === this.pdfPreviewUrl) { this.previewVersionNumber = null; }
    } finally {
      if (previewUrl === this.pdfPreviewUrl) { this.previewVersionLoading = false; }
    }
  }

  async setDisplayDate() {
    this.displayDate = this.ptLuxonDate.dateTime.toFormat('dd/MM/yyyy');
    this.displayDay = this.ptLuxonDate.dateTime.toFormat('EEEE, d MMMM yyyy');
  }

  async downloadDprPreview() {
    if (this.previewDownloadLoading || !this.currentVesselId || !this.currentSetupConfigured
      || !this.canViewReport || this.summaryLoading || this.busy) { return; }
    this.previewDownloadLoading = true;
    try {
      const date = new PtLuxonDate(this.ptLuxonDate.dateTime);
      const file = await this.dprService.getDprPreviewFile(date, this.currentVesselId, this.config.isImplementerEnvironment);
      this.saveDprFile(file);
    } catch {
      await this.showDownloadError('Unable to download the DPR preview. Please try again.');
    } finally {
      this.previewDownloadLoading = false;
    }
  }

  async downloadCurrentDpr() {
    if (this.currentDownloadLoading || !this.hasCurrentDpr || !this.canViewReport || this.summaryLoading || this.busy) { return; }
    this.currentDownloadLoading = true;
    try {
      const file = await this.dprService.getCurrentDprFile(new PtLuxonDate(this.ptLuxonDate.dateTime));
      this.saveDprFile(file);
    } catch {
      await this.showDownloadError('Unable to download the current DPR. Please try again.');
    } finally {
      this.currentDownloadLoading = false;
    }
  }

  private async showDownloadError(message: string) {
    await this.toastCtrl.create({ message, cssClass: 'toast-danger', duration: 5000,
      position: 'bottom', showCloseButton: true, closeButtonText: 'Close' }).present();
  }

  async generateDprClicked() {
    if (this.completedSuccessfully || this.completeDayLoading || this.continuingDay || (this.completeDay && !this.canGenerateAndComplete)) {
      return;
    }
    if (!this.completeDay) {
      this.downloadDpr();
      return;
    }
    this.completeDayLoading = true;
    this.completeDayError = null;
    try {
      // Both requests must target the day whose completion requirements were checked.
      const date = new PtLuxonDate(this.workflowDate);
      const result = await this.dprService.generateDpr(date, this.currentVesselId, this.config.isImplementerEnvironment);
      await this.saveCompleteDay(date);
      this.completedSuccessfully = true;
      this.navHelperService.updateDailyLogRequiresReload(true);
      try {
        this.saveDprFile(result);
      } catch {
        await this.showDownloadError('The day was completed, but the DPR could not be downloaded. Download the current DPR from the Daily Log.');
      }
      try {
        await this.returnToLog(date.dateTime);
      } catch {
        this.completeDayError = 'The day was completed. Please use Return to Daily Log to return.';
      }
    } catch (error) {
      if (this.completedSuccessfully) {
        this.completeDayError = 'The day was completed. Please use Return to Daily Log to return.';
        return;
      }
      // Refresh stale checks after a server rejection and keep the workflow open.
      this.completeDayLoading = false;
      this.reportContextStale = true;
      await this.refreshWorkflow();
      this.completeDayError = 'The day could not be completed. Review the refreshed checks and completion requirements, then try again.';
    } finally {
      this.completeDayLoading = false;
      this.resizeContent();
    }
  }

  private async saveCompleteDay(date: PtLuxonDate) {
    const editor = new CompleteDayEditor();

    editor.isComplete = true;
    editor.ptLuxonDate = new PtLuxonDate(date.dateTime);
    // if not in implementer environment, then we want to save change history for current day
    // (if in implementer this gets saved on merge instead of here)
    await this.completeDayService.saveCompleteDay(editor, !this.config.isImplementerEnvironment);
  }

  private downloadDpr(signatureDate?: DateTime) {
    const downloadLink = this.dprService
      .getNewDprDownloadLink(this.ptLuxonDate, this.currentVesselId, this.config.isImplementerEnvironment, signatureDate);

    setTimeout(() => {
      location.href = downloadLink;
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/daily-progress-report/daily-progress-report-preview/daily-progress-report-preview.ts